import * as THREE from 'three';
import { APP_CONFIG } from '../config/app';
import { store } from './store';
import { EngineRenderer } from '../engine/renderer';
import { EngineLoop } from '../engine/loop';
import { CameraRig } from '../engine/cameraRig';
import { EnvironmentSystem } from '../engine/environment';
import { WeatherSystem } from '../engine/weather';
import { SceneManager } from '../scenes/sceneManager';
import { YulongScene } from '../scenes/yulong/index';
import { LijiangScene } from '../scenes/lijiang/index';
import { ErhaiScene } from '../scenes/erhai/index';
import { PostProcessingPipeline } from '../engine/post';
import { AudioPlayer } from '../audio/player';
import { HudUI } from '../ui/hud';
import { InputManager } from '../engine/input';
import { DebugOverlay } from '../engine/debug';
import { showToast } from '../ui/toast';
import { t } from '../ui/i18n';
import { QUALITY_CONFIGS } from '../config/quality';

console.info(`${APP_CONFIG.title} (${APP_CONFIG.vietnameseTitle}) starting v${APP_CONFIG.version}...`);

async function bootstrap() {
  const canvas = document.getElementById('webgl-canvas') as HTMLCanvasElement;
  if (!canvas) {
    console.error('Canvas element #webgl-canvas not found');
    return;
  }

  // 1. Progress updater
  const progressBar = document.getElementById('progress-bar');
  const progressText = document.getElementById('progress-text');
  const startBtnContainer = document.getElementById('start-button-container');
  const btnStart = document.getElementById('btn-start');
  const loadingScreen = document.getElementById('loading-screen');

  const updateProgress = (pct: number, msg?: string) => {
    const percent = Math.min(100, Math.round(pct * 100));
    if (progressBar) progressBar.style.width = `${percent}%`;
    if (progressText) {
      progressText.innerText = msg ? `${msg} ${percent}%` : `Đang chuẩn bị không gian ${percent}%`;
    }
  };

  updateProgress(0.15, 'Đang chuẩn bị đồ họa');

  // 2. Engine components
  const engineRenderer = new EngineRenderer(canvas);
  const engineLoop = new EngineLoop();
  const cameraRig = new CameraRig(canvas, engineRenderer.getAspectRatio());
  const environment = new EnvironmentSystem();
  const weather = new WeatherSystem();
  const audioPlayer = new AudioPlayer();
  const debugOverlay = new DebugOverlay();

  // Resize handling
  engineRenderer.onResizeCallback((_w, _h) => {
    cameraRig.setAspectRatio(engineRenderer.getAspectRatio());
  });

  updateProgress(0.4, 'Đang tải âm thanh & môi trường');

  // 3. Scene graph
  const rootScene = new THREE.Scene();
  rootScene.add(environment.root);
  rootScene.add(weather.root);
  rootScene.fog = environment.fog;

  // 4. Scene manager
  const sceneManager = new SceneManager(engineRenderer.renderer, cameraRig.camera, showToast);
  sceneManager.getBoatMoveVector = () => cameraRig.boatMoveVector;
  rootScene.add(sceneManager.sceneContainer);

  // Post Processing Pipeline
  const postPipeline = new PostProcessingPipeline(engineRenderer.renderer, rootScene, cameraRig.camera);
  engineRenderer.onResizeCallback((w, h) => {
    postPipeline.setSize(w, h);
  });

  // Register all 3 Yunnan scenes
  sceneManager.register('yulong', async () => new YulongScene());
  sceneManager.register('lijiang', async () => new LijiangScene());
  sceneManager.register('erhai', async () => new ErhaiScene());

  updateProgress(0.65, 'Đang tạo cảnh 3D diorama');

  // 5. Initial Scene Load
  const initialSceneId = store.getState().sceneId || APP_CONFIG.defaultScene;
  const initialEnvState = {
    timeOfDay: store.getState().timeOfDay,
    weather: store.getState().weather,
    windSpeed: 1.0,
    emissiveFactor: environment.getEmissiveFactor(),
    quality: QUALITY_CONFIGS.medium,
  };

  const loadedScene = await sceneManager.switchScene(initialSceneId, initialEnvState, (p) => {
    updateProgress(0.65 + p * 0.3);
  });

  cameraRig.setView(loadedScene.cameras[0], true);

  // 6. UI HUD & Input
  const hud = new HudUI(cameraRig, environment, weather, audioPlayer);
  hud.setViews(loadedScene.cameras);

  const photoCaptureHandler = () => {
    hud.toggleHud();
    requestAnimationFrame(() => {
      engineRenderer.renderer.render(rootScene, cameraRig.camera);
      const dataUrl = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = `yunnan-dreamscape-${store.getState().sceneId}-${Date.now()}.png`;
      a.click();
      hud.toggleHud();
      showToast('Đã lưu ảnh chụp postcard vào máy!');
    });
  };

  new InputManager(cameraRig, hud, audioPlayer, photoCaptureHandler);

  // Photo mode button on top HUD
  document.getElementById('btn-photo-mode')?.addEventListener('click', photoCaptureHandler);

  // 7. Hash change router
  window.addEventListener('hashchange', async () => {
    const hash = window.location.hash;
    const match = hash.match(/scene=([a-z0-9_-]+)/i);
    if (match && match[1]) {
      const targetSceneId = match[1];
      if (targetSceneId !== store.getState().sceneId) {
        showToast(`Đang chuyển tới cảnh mới...`);
        const envState = {
          timeOfDay: store.getState().timeOfDay,
          weather: store.getState().weather,
          windSpeed: 1.0,
          emissiveFactor: environment.getEmissiveFactor(),
          quality: QUALITY_CONFIGS.medium,
        };
        const nextScene = await sceneManager.switchScene(targetSceneId, envState, () => {});
        hud.setViews(nextScene.cameras);
        cameraRig.setView(nextScene.cameras[0]);
      }
    }
  });

  // 8. Main Render Loop
  engineLoop.register((dt: number) => {
    cameraRig.update(dt);
    environment.update(dt);
    weather.update(dt, cameraRig.camera.position);

    const envState = {
      timeOfDay: store.getState().timeOfDay,
      weather: store.getState().weather,
      windSpeed: 1.0,
      emissiveFactor: environment.getEmissiveFactor(),
      quality: QUALITY_CONFIGS.medium,
    };
    sceneManager.update(dt, envState);

    postPipeline.setCamera(cameraRig.camera);
    postPipeline.render(dt);
    debugOverlay.update(engineRenderer.renderer, engineLoop);
  });

  engineLoop.start();

  // 9. Finish loading screen
  updateProgress(1.0, t('loading_ready', store.getState().language));
  if (startBtnContainer) {
    startBtnContainer.classList.remove('hidden');
  }

  // User gesture start
  btnStart?.addEventListener('click', async () => {
    await audioPlayer.unlock();
    audioPlayer.play();
    loadingScreen?.classList.add('fade-out');
    showToast(t('loading_hint', store.getState().language), 4500);
  });
}

window.addEventListener('DOMContentLoaded', bootstrap);
